import {
  createListSearchParams,
  ListOrder,
  ListPagination,
} from 'api/list-params'
import { pipe } from 'fp-ts/function'
import * as t from 'io-ts'
import {
  decodeBlob,
  decodeHeaders,
  decodeJson,
  decodeJsonWithTotal,
  del,
  get,
  patch,
  post,
  put,
} from 'lib/request'

import {
  TColumns,
  TDuplicatedTerms,
  TTerm,
  TTermImportItems,
  TTermKeysDirectoriesList,
} from './terms.codecs'

export type GetTermDetailsInput = {
  projectId: string
  termId: string
}

export const getTermDetails = async ({
  projectId,
  termId,
}: GetTermDetailsInput) => {
  return pipe(
    await get('api/projects/:projectId/terms/:termId', {
      params: { projectId, termId },
    }),
    decodeJson(TTerm),
  )
}

export const createTerm = async ({
  projectId,
  description,
  key,
  tags,
  translations,
  link,
}: {
  projectId: string
  description: string
  key: string
  tags: Array<string>
  translations: Array<{ lang: string; text: string }>
  link: boolean
}) => {
  return pipe(
    await post('api/projects/:projectId/terms', {
      params: { projectId },
      body: {
        key,
        tags,
        description,
        translations,
        link,
      },
    }),
    decodeJson(TTerm),
  )
}

export const editTerm = async ({
  projectId,
  termId,
  description,
  key,
  tags,
  translations,
  link,
}: {
  projectId: string
  termId: string
  description: string
  key: string
  tags: Array<string>
  translations: Array<{ lang: string; text: string }>
  link: boolean
}) => {
  return pipe(
    await put('api/projects/:projectId/terms/:termId', {
      params: { projectId, termId },
      body: {
        key,
        tags,
        description,
        translations,
        link,
      },
    }),
  )
}

export const editTranslation = async ({
  projectId,
  termId,
  lang,
  text,
}: {
  projectId: string
  termId: string
  lang: string
  text: string
}) => {
  return pipe(
    await patch('api/projects/:projectId/terms/:termId/translations', {
      params: { projectId, termId },
      body: {
        lang,
        text,
      },
    }),
  )
}

export type GetTermsListInput = {
  projectId: string
  pagination: ListPagination
  search: string
  order: ListOrder
  empty: boolean
  keyPrefix: string | null
}

export const getProjectTerms = async ({
  projectId,
  pagination,
  search,
  order,
  empty,
  keyPrefix,
}: GetTermsListInput) => {
  const params = createListSearchParams({ pagination, order })

  params.set('search', search)
  params.set('empty', empty.toString())

  if (keyPrefix) params.set('key_prefix', keyPrefix)

  return pipe(
    await get('api/projects/:projectId/terms', {
      params: { projectId },
      query: params,
    }),
    decodeJsonWithTotal(TTerm, pagination),
  )
}

export type GetTermsColumnsInput = { projectId: string }

export const getTermsColumns = async ({ projectId }: GetTermsColumnsInput) => {
  return pipe(
    await get('api/projects/:projectId/terms/columns', {
      params: { projectId },
    }),
    decodeJson(TColumns),
  )
}

export const editTermsColumns = async ({
  projectId,
  showColumns,
}: {
  projectId: string
  showColumns: Array<string>
}) => {
  return pipe(
    await put('api/projects/:projectId/terms/columns', {
      params: { projectId },
      body: { showColumns },
    }),
  )
}

export const removeTerm = async ({
  projectId,
  termId,
}: {
  projectId: string
  termId: string
}) => {
  return pipe(
    await del('api/projects/:projectId/terms/:termId', {
      params: { projectId, termId },
    }),
  )
}

export const importTerms = async ({
  projectId,
  lang,
  file,
  override,
  placeholder,
  namedLeftPlaceholder,
  namedRightPlaceholder,
}: {
  projectId: string
  lang: string
  file: Blob
  override?: boolean
  placeholder?: string
  namedLeftPlaceholder?: string
  namedRightPlaceholder?: string
}) => {
  return pipe(
    await post('api/projects/:projectId/terms/import', {
      type: 'file',
      params: { projectId },
      query: new URLSearchParams({
        lang,
        ...(override !== undefined && {
          override: override.toString(),
        }),

        ...(placeholder !== undefined && {
          placeholder: placeholder,
        }),

        ...(namedLeftPlaceholder !== undefined && {
          named_left_placeholder: namedLeftPlaceholder,
        }),

        ...(namedRightPlaceholder !== undefined && {
          named_right_placeholder: namedRightPlaceholder,
        }),
      }),
      body: {
        file,
      },
    }),
    decodeJson(TTermImportItems),
  )
}
export type ExportFormat = 'json' | 'xml' | 'ios'

export const exportTerms = async ({
  projectId,
  lang,
  format,
  placeholder,
  namedLeftPlaceholder,
  namedRightPlaceholder,
}: {
  projectId: string
  lang: string
  format: ExportFormat
  placeholder?: string
  namedLeftPlaceholder?: string
  namedRightPlaceholder?: string
}) => {
  return pipe(
    await get('api/projects/:projectId/terms/export', {
      params: { projectId },
      query: new URLSearchParams({
        lang,
        format,

        ...(placeholder !== undefined && {
          placeholder: placeholder,
        }),

        ...(namedLeftPlaceholder !== undefined && {
          named_left_placeholder: namedLeftPlaceholder,
        }),

        ...(namedRightPlaceholder !== undefined && {
          named_right_placeholder: namedRightPlaceholder,
        }),
      }),
    }),
    async response => {
      const blob = await decodeBlob(response)
      const headers = await decodeHeaders(
        t.strict({
          'file-name': t.string,
        }),
      )(response)

      return {
        blob,
        fileName: headers['file-name'],
      }
    },
  )
}

export const importTermsFromFigma = async ({
  projectId,
  lang,
  override,
}: {
  projectId: string
  lang: string
  override: boolean
}) => {
  return pipe(
    await post('api/projects/:projectId/figma/import', {
      params: { projectId },
      query: new URLSearchParams({ lang, override: override.toString() }),
    }),
    decodeJson(TTermImportItems),
  )
}

export type GetTermDuplicatesInput = {
  projectId: string
  lang: string
  text: string
}

export const getTermDuplicates = async ({
  projectId,
  lang,
  text,
}: GetTermDuplicatesInput) => {
  return pipe(
    await get('api/projects/:projectId/terms/duplicates', {
      params: { projectId },
      query: new URLSearchParams({ lang, text }),
    }),
    decodeJson(TDuplicatedTerms),
  )
}

export type GetTermKeysDirectoriesListInput = { projectId: string }

export const getTermKeysDirectoriesList = async ({
  projectId,
}: GetTermKeysDirectoriesListInput) => {
  return pipe(
    await get('api/projects/:projectId/terms/directories', {
      params: { projectId },
    }),
    decodeJson(TTermKeysDirectoriesList),
  )
}
